import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import {
  createPlaceSchema,
  updatePlaceSchema,
  type CreatePlaceInput,
  type UpdatePlaceInput,
} from "@releve/shared";
import { FullscreenFormDialog } from "@/components/ui/fullscreen-form-dialog";
import { useEmissionRegions } from "@/hooks/usePlaceUtilities";
import { useFeatureEnabled } from "@/hooks/useOrganizationFeatures";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { DangerZone } from "@/components/ui/danger-zone";
import { Switch } from "@/components/ui/switch";
import { NumberInput } from "@/components/ui/number-input";
import { DatePicker } from "@/components/ui/date-picker";
import { OccupancyClassificationSelect } from "@/components/places/ui/occupancy-classification-select";
import { DeletePlaceDialog } from "@/components/places/DeletePlaceDialog";
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { useAddressLookup, type ConfidenceSource } from "@/hooks/useAddressLookup";
import {
  usePlaceIndex,
  useCreatePlace,
  useUpdatePlace,
  type PlaceDetail,
} from "@/hooks/usePlaces";
import { toast } from "@/hooks/useToast";
import { captureException } from "@/lib/sentry";
import { format } from "date-fns";
import { parseLocalISODate } from "@/lib/dateUtils";
import { MapPin, Loader2 } from "lucide-react";
import { placeInheritsParentAddress } from "./placeAddressInheritance";
import { PlaceMapPickerDialog } from "./PlaceMapPickerDialog";
import { gisToCenter, gisToLocationData } from "./placeMapLocation";
import type { LocationData } from "@/components/ui/mapbox-location-picker";
import type { AzureAddress } from "@/lib/types/azure";
import type { Geometry } from "geojson";

/** `saveLocation`'s argument — what a pending pick holds until Submit. */
type SaveLocationInput = Parameters<ReturnType<typeof useAddressLookup>["saveLocation"]>[0];

interface CreatePlaceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPlaceCreated?: (place: { id: string; code: string; name: string }) => void;
  /** Pre-set values, e.g. parent for "Add sub-space" or gis id from the Addresses tab. */
  initialValues?: Partial<CreatePlaceInput>;
  /** If provided, we're editing (repo convention: one create/edit modal).
   *  Edit mode hides the create-only sections — parent/level/classification
   *  (managed on Facilities), initial tenure (managed on Overview) — and
   *  PATCHes only the dirty fields. */
  place?: PlaceDetail | null;
  onSaved?: () => void;
  /** Edit mode: called after the danger-zone delete succeeds (both dialogs
   *  close first) — the caller navigates away from the dead record. */
  onDeleted?: () => void;
}

/** Create shape + the edit-only fields (updatePlaceSchema has is_active and
 * emission_region_code; createPlaceSchema doesn't). The active resolver strips
 * whatever the mode's schema doesn't know. */
type FormValues = CreatePlaceInput & {
  is_active?: boolean;
  emission_region_code?: string | null;
};

const EMISSION_REGION_NONE = "__none__";

const TENURE_TYPES = [
  "owned",
  "long_term_lease",
  "short_term_rental",
  "partnership_in_kind",
  "public_space_permit",
  "other",
] as const;

/** Allowed premises levels given the selected parent (Places plan: derive from
 * the parent's level — never hardcode 'section'). */
function allowedLevels(
  parent: { premises_level: "site" | "building" | "section" } | undefined,
): Array<"site" | "building" | "section"> {
  if (!parent) return ["site", "building"];
  if (parent.premises_level === "site") return ["building", "section"];
  return ["section"]; // parent building; sections can't be parents (DB enforces)
}

export function CreatePlaceDialog({
  open,
  onOpenChange,
  onPlaceCreated,
  initialValues,
  place,
  onSaved,
  onDeleted,
}: CreatePlaceDialogProps) {
  const { t } = useTranslation("places");
  const createPlace = useCreatePlace();
  const updatePlace = useUpdatePlace();
  const isEdit = !!place;
  const { enabled: emissionsEnabled } = useFeatureEnabled("places_emissions");
  // Regions only load when the field can render (edit mode + flag on).
  const emissionRegions = useEmissionRegions(isEdit && emissionsEnabled);
  const { data: places = [] } = usePlaceIndex();
  const [includeTenure, setIncludeTenure] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [addressQuery, setAddressQuery] = useState("");
  // "Use a different address" on a child place: reveals the picker while the
  // place is still on its parent's address.
  const [addressOverride, setAddressOverride] = useState(false);
  const [savedAddressLabel, setSavedAddressLabel] = useState<string | null>(
    place?.gis_location?.formatted_address ?? null,
  );
  // The location picked in this dialog session (address or map), so the label
  // knows what it is and "Edit on map" reopens on it before it is saved.
  const [pickedLocation, setPickedLocation] = useState<LocationData | null>(null);
  // A picked location is saved only when the place is (Submit) — never on
  // pick, so a cancelled form or a re-pick leaves no orphan gis_locations row
  // behind (it would show in Places → Addresses). The exact saveLocation
  // argument, incl. the search text at pick time.
  const [pendingPick, setPendingPick] = useState<SaveLocationInput | null>(null);
  const [mapPickerOpen, setMapPickerOpen] = useState(false);

  const {
    setSearchQuery,
    searchResults,
    isSearching,
    saveLocation,
    isSaving,
    formatAddress,
  } = useAddressLookup({ autoValidate: true, cacheResults: true });

  const form = useForm<FormValues>({
    // The mode's schema validates; parsed values only carry its fields.
    resolver: zodResolver(isEdit ? updatePlaceSchema : createPlaceSchema),
    defaultValues: place
      ? {
          // Edit mode validates with updatePlaceSchema — only seed the fields
          // the patch can actually read (create-only fields are hidden and
          // would be stripped by the resolver anyway).
          name: place.name,
          description: place.description,
          gis_location_id: place.gis_location_id,
          gross_floor_area_source_value: place.gross_floor_area_source_value,
          gross_floor_area_source_unit: place.gross_floor_area_source_unit,
          ubid: place.ubid,
          notes: place.notes,
          emission_region_code: place.emission_region_code,
          is_active: place.is_active,
          // parent drives the inherited-address display in edit mode.
          parent_place_id: place.parent_place_id,
        }
      : {
          name: "",
          bedes_occupancy_classification: "",
          premises_level: "building",
          construction_status: "existing",
          gis_location_id: null,
          parent_place_id: null,
          tenure: null,
          ...initialValues,
        },
  });

  const parentId = form.watch("parent_place_id");
  const premisesLevel = form.watch("premises_level");

  const parentOptions = useMemo(
    () => places.filter((p) => p.premises_level !== "section"),
    [places],
  );
  const parent = parentOptions.find((p) => p.id === parentId);
  const levels = allowedLevels(parent);
  const isSection = premisesLevel === "section";
  // Sub-space wording: create mode follows the level picker (the "Add
  // sub-space" flow pre-seeds section); edit mode follows the saved record
  // (premises_level isn't part of the edit form).
  const isSubSpace = isEdit ? place?.premises_level === "section" : isSection;

  // Location inheritance is a DEFAULT, not a lock (migration 20260819150000) —
  // the rule and its one approximation live in placeAddressInheritance.ts.
  const currentLocationId = form.watch("gis_location_id");
  // A pending pick is this place's own location, not the parent's.
  const inheritsAddress = !pendingPick && placeInheritsParentAddress(currentLocationId, parent);

  const handleParentChange = (value: string) => {
    const next = value === "none" ? null : value;
    form.setValue("parent_place_id", next);
    const nextParent = parentOptions.find((p) => p.id === next);
    const nextLevels = allowedLevels(nextParent);
    if (!nextLevels.includes(form.getValues("premises_level"))) {
      form.setValue("premises_level", nextLevels[0]);
    }
    if (nextParent) {
      // Ownership is still inherited (locked) — the DB trigger overwrites
      // whatever the client sends, so tenure can't be set on a child.
      // The ADDRESS is deliberately kept: since migration 20260819150000 a
      // child's own address wins over its parent's, so an address the user
      // already chose stays theirs when the place is nested.
      setIncludeTenure(false);
      form.setValue("tenure", null);
    }
    // Re-derive the inherit/override display against the NEW parent.
    setAddressOverride(false);
  };

  /** A pick (address suggestion or map) — held until Submit, see `pendingPick`. */
  const handleAddressSelect = (suggestion: {
    address: AzureAddress;
    coordinates: { latitude: number; longitude: number };
    confidence?: number;
    confidenceSource?: ConfidenceSource;
    placeId?: string;
    /** From "Pick on map": a pin `Point` or a drawn `Polygon`. */
    geom?: Geometry;
  }) => {
    const { address } = suggestion;
    const label = formatAddress(address);
    setPendingPick({
      address,
      coordinates: suggestion.coordinates,
      originalInput: addressQuery,
      confidence: suggestion.confidence,
      confidenceSource: suggestion.confidenceSource,
      placeId: suggestion.placeId,
      geom: suggestion.geom,
    });
    setSavedAddressLabel(label);
    setPickedLocation({ address, coordinates: suggestion.coordinates, geom: suggestion.geom });
    if (!form.getValues("name")) form.setValue("name", label);
    setAddressQuery("");
    setSearchQuery("");
  };

  /**
   * Back to no own location: on a child the DB re-inherits the parent's (a
   * NULL), on a root place the picker reopens. Drops any pending pick.
   */
  const dropPick = () => {
    form.setValue("gis_location_id", null, { shouldDirty: true });
    setSavedAddressLabel(null);
    setPickedLocation(null);
    setPendingPick(null);
    setAddressQuery("");
    setAddressOverride(false);
  };

  /**
   * Save a pending pick (Submit only) and point the form at it. On failure the
   * pick stays pending for a retry; on a retried Submit after a failed PLACE
   * save, the stored id is reused — nothing is saved twice.
   */
  const savePendingPick = async (): Promise<string | null | undefined> => {
    if (!pendingPick) return undefined;
    try {
      const id = (await saveLocation(pendingPick)) as string;
      form.setValue("gis_location_id", id, { shouldDirty: true });
      setPendingPick(null);
      return id;
    } catch (error) {
      captureException(error, { feature: "place-map", action: "save-location" });
      toast.error(t("create.addressSaveFailed"), {
        description: error instanceof Error ? error.message : undefined,
      });
      return null;
    }
  };

  // "Pick on map" opens on the location picked in this session, else the
  // place's own saved one (so a drawn area can be redrawn), else on the
  // parent's pin (a camp starts at its event site).
  const ownSavedGis =
    place?.gis_location && currentLocationId === place.gis_location_id && !inheritsAddress
      ? place.gis_location
      : null;
  const mapInitialLocation: LocationData | undefined =
    pickedLocation ?? gisToLocationData(ownSavedGis);
  const mapInitialCenter = gisToCenter(parent);

  const savedGeomType: Geometry["type"] | null = pickedLocation
    ? (pickedLocation.geom?.type ?? null)
    : (ownSavedGis?.geom?.type ?? null);
  const savedLocationText =
    savedGeomType === "Polygon"
      ? t("fields.locationDrawnArea", { address: savedAddressLabel })
      : savedGeomType === "Point"
        ? t("fields.locationMapPin", { address: savedAddressLabel })
        : savedAddressLabel;

  const pickOnMapButton = (label: string) => (
    <button
      type="button"
      className="text-xs text-muted-foreground hover:text-foreground underline whitespace-nowrap"
      onClick={() => setMapPickerOpen(true)}
    >
      {label}
    </button>
  );

  const handleSubmit = async (values: FormValues) => {
    const savedPickId = await savePendingPick();
    if (savedPickId === null) return; // location not saved (toasted); form and pick kept
    if (savedPickId) values = { ...values, gis_location_id: savedPickId };
    if (isEdit && place) {
      // PATCH only what the user actually touched: sending an unchanged GFA
      // would trip the 409 detail-precedence guard, and sparse patches keep
      // the audit diff honest. Value + unit travel together (the backend
      // derives the canonical m² from the pair).
      const dirty = form.formState.dirtyFields as Partial<
        Record<keyof UpdatePlaceInput, unknown>
      >;
      const patch: UpdatePlaceInput = {};
      const gfaDirty =
        dirty.gross_floor_area_source_value || dirty.gross_floor_area_source_unit;
      if (dirty.name && values.name != null) patch.name = values.name;
      if (dirty.description) patch.description = values.description ?? null;
      if (savedPickId || dirty.gis_location_id) patch.gis_location_id = values.gis_location_id;
      if (gfaDirty) {
        patch.gross_floor_area_source_value =
          values.gross_floor_area_source_value ?? null;
        patch.gross_floor_area_source_unit =
          values.gross_floor_area_source_unit ?? "m2";
      }
      if (dirty.ubid) patch.ubid = values.ubid ?? null;
      if (dirty.notes) patch.notes = values.notes ?? null;
      if (dirty.emission_region_code)
        patch.emission_region_code = values.emission_region_code ?? null;
      if (dirty.is_active) patch.is_active = values.is_active;
      try {
        if (Object.keys(patch).length > 0) {
          await updatePlace.mutateAsync({ placeId: place.id, patch });
        }
        toast.success(t("edit.success"));
        onOpenChange(false);
        onSaved?.();
      } catch {
        // useUpdatePlace's onError already toasts.
      }
      return;
    }
    const payload: CreatePlaceInput = {
      ...(values as CreatePlaceInput),
      tenure: includeTenure && !isSection ? values.tenure : null,
    };
    try {
      const result = await createPlace.mutateAsync(payload);
      toast.success(t("create.success", { name: values.name }));
      form.reset();
      onOpenChange(false);
      onPlaceCreated?.(result.place);
    } catch {
      // useCreatePlace's onError already toasts.
    }
  };

  const errorClass = (hasError: boolean) => (hasError ? "border-destructive" : "");

  return (
    <FullscreenFormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={
        isEdit
          ? t(isSubSpace ? "edit.titleSubSpace" : "edit.title")
          : t(isSubSpace ? "create.titleSubSpace" : "create.title")
      }
      description={
        isEdit
          ? t(isSubSpace ? "edit.descriptionSubSpace" : "edit.description")
          : t(isSubSpace ? "create.descriptionSubSpace" : "create.description")
      }
      submitLabel={
        isEdit
          ? t("common.save")
          : t(isSubSpace ? "create.submitSubSpace" : "create.submit")
      }
      isSubmitting={isSaving || (isEdit ? updatePlace.isPending : createPlace.isPending)}
      onSubmit={form.handleSubmit(handleSubmit)}
      formId="create-place-form"
    >
      <Form {...form}>
        <div className="space-y-8 max-w-4xl mx-auto">
          {/* Associations row — outside the document card. Create-only:
              parent/level are not patchable and classification is managed on
              the Facilities tab. */}
          {!isEdit && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField
              control={form.control}
              name="parent_place_id"
              render={({ field, fieldState }) => (
                <FormItem>
                  <div className="flex items-center gap-2">
                    <FormLabel>{t("fields.parentPlace")}</FormLabel>
                    {fieldState.error && (
                      <span className="text-xs text-destructive">{fieldState.error.message}</span>
                    )}
                  </div>
                  <Select
                    value={field.value ?? "none"}
                    onValueChange={handleParentChange}
                  >
                    <FormControl>
                      <SelectTrigger className={errorClass(!!fieldState.error)}>
                        <SelectValue placeholder={t("fields.parentPlaceNone")} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">{t("fields.parentPlaceNone")}</SelectItem>
                      {parentOptions.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name} ({p.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="premises_level"
              render={({ field, fieldState }) => (
                <FormItem>
                  <div className="flex items-center gap-2">
                    <FormLabel>{t("fields.premisesLevel")}</FormLabel>
                    {fieldState.error && (
                      <span className="text-xs text-destructive">{fieldState.error.message}</span>
                    )}
                  </div>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className={errorClass(!!fieldState.error)}>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {levels.map((level) => (
                        <SelectItem key={level} value={level}>
                          {t(`premisesLevel.${level}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="bedes_occupancy_classification"
              render={({ field, fieldState }) => (
                <FormItem>
                  <div className="flex items-center gap-2">
                    <FormLabel>{t("fields.placeType")}</FormLabel>
                    {fieldState.error && (
                      <span className="text-xs text-destructive">{fieldState.error.message}</span>
                    )}
                  </div>
                  <FormControl>
                    <OccupancyClassificationSelect
                      value={field.value}
                      onChange={field.onChange}
                      placeholder={t("fields.placeTypePlaceholder")}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          )}

          {/* The document card */}
          <Card>
            <CardContent className="pt-6 space-y-8">
              <h2 className="text-[40px] font-extralight leading-tight text-right mb-3 uppercase">
                {t(isSubSpace ? "create.heroTitleSubSpace" : "create.heroTitle")}
              </h2>

              <FormField
                control={form.control}
                name="name"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel>{t("fields.name")}</FormLabel>
                      {fieldState.error && (
                        <span className="text-xs text-destructive">{fieldState.error.message}</span>
                      )}
                    </div>
                    <FormControl>
                      <Input
                        className={errorClass(!!fieldState.error)}
                        placeholder={t("fields.namePlaceholder")}
                        {...field}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              {/* Address band */}
              <div className="-mx-6 px-6 py-6 bg-muted space-y-3">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t("sections.address")}
                </h3>
                {inheritsAddress && !addressOverride ? (
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                      <MapPin className="h-4 w-4 shrink-0" />
                      <span>
                        {parent!.formatted_address
                          ? t("fields.addressInheritedFrom", {
                              parent: parent!.name,
                              address: parent!.formatted_address,
                            })
                          : t("fields.addressInherited", { parent: parent!.name })}
                      </span>
                    </p>
                    <div className="flex items-center gap-3">
                      {pickOnMapButton(t("fields.locationPickOnMap"))}
                      <button
                        type="button"
                        className="text-xs text-muted-foreground hover:text-foreground underline whitespace-nowrap"
                        onClick={() => {
                          setAddressOverride(true);
                          setSavedAddressLabel(null);
                          setAddressQuery("");
                        }}
                      >
                        {t("fields.addressUseDifferent")}
                      </button>
                    </div>
                  </div>
                ) : savedAddressLabel ? (
                  <div className="flex items-center justify-between">
                    <p className="text-sm flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      {savedLocationText}
                    </p>
                    <div className="flex items-center gap-3">
                      {pickOnMapButton(t("fields.locationEditOnMap"))}
                      <button
                        type="button"
                        className="text-xs text-muted-foreground hover:text-foreground underline"
                        onClick={dropPick}
                      >
                        {t("fields.addressChange")}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex justify-end gap-3">
                      {pickOnMapButton(t("fields.locationPickOnMap"))}
                      {parent && (
                        <button
                          type="button"
                          className="text-xs text-muted-foreground hover:text-foreground underline"
                          onClick={dropPick}
                        >
                          {t("fields.addressUseInherited", { parent: parent.name })}
                        </button>
                      )}
                    </div>
                    <Input
                      value={addressQuery}
                      placeholder={t("fields.addressPlaceholder")}
                      onChange={(e) => {
                        setAddressQuery(e.target.value);
                        setSearchQuery(e.target.value);
                      }}
                    />
                    {isSearching && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        {t("fields.addressSearching")}
                      </p>
                    )}
                    {addressQuery.length > 2 && searchResults.length > 0 && (
                      <Command className="border rounded-md">
                        <CommandList>
                          <CommandEmpty>{t("fields.addressNoResults")}</CommandEmpty>
                          <CommandGroup>
                            {searchResults.slice(0, 5).map((s, i) => (
                              <CommandItem
                                key={s.placeId ?? i}
                                onSelect={() => handleAddressSelect(s)}
                              >
                                <MapPin className="h-4 w-4 mr-2 text-muted-foreground" />
                                {formatAddress(s.address)}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    )}
                  </div>
                )}
              </div>

              {/* Physical profile */}
              <div className="space-y-3">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t("sections.physical")}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <FormField
                    control={form.control}
                    name="gross_floor_area_source_value"
                    render={({ field, fieldState }) => (
                      <FormItem className="md:col-span-2">
                        <div className="flex items-center gap-2">
                          <FormLabel>{t("fields.grossFloorArea")}</FormLabel>
                          {fieldState.error && (
                            <span className="text-xs text-destructive">{fieldState.error.message}</span>
                          )}
                        </div>
                        <FormControl>
                          <NumberInput
                            className={errorClass(!!fieldState.error)}
                            value={field.value ?? ""}
                            onChange={(v) => field.onChange(v === "" ? null : Number(v))}
                            min={0}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="gross_floor_area_source_unit"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("fields.areaUnit")}</FormLabel>
                        <Select
                          value={field.value ?? "m2"}
                          onValueChange={field.onChange}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="m2">m²</SelectItem>
                            <SelectItem value="ft2">ft²</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormItem>
                    )}
                  />
                  {/* year_built / occupancy_pct are effective-dated profile
                      details now (R3) — recorded after creation via the
                      profile editor, not create-time columns. */}
                  <FormField
                    control={form.control}
                    name="ubid"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <div className="flex items-center gap-2">
                          <FormLabel>{t("fields.ubid")}</FormLabel>
                          {fieldState.error && (
                            <span className="text-xs text-destructive">{fieldState.error.message}</span>
                          )}
                        </div>
                        <FormControl>
                          <Input
                            className={errorClass(!!fieldState.error)}
                            value={field.value ?? ""}
                            onChange={(e) => field.onChange(e.target.value || null)}
                            disabled={isSection}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  {/* Place-level emission-region default (places_emissions,
                      edit mode): beats the address-derived region, is beaten
                      by a per-meter override. */}
                  {isEdit && emissionsEnabled && (
                    <FormField
                      control={form.control}
                      name="emission_region_code"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("fields.emissionRegion")}</FormLabel>
                          <Select
                            value={(field.value as string | null) ?? EMISSION_REGION_NONE}
                            onValueChange={(next) =>
                              field.onChange(next === EMISSION_REGION_NONE ? null : next)
                            }
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value={EMISSION_REGION_NONE}>
                                {t("fields.emissionRegionAuto")}
                              </SelectItem>
                              {/* A saved code must stay visible while regions
                                  load (or if it left the vocabulary) — a blank
                                  trigger hides an active override. Conditional:
                                  a duplicate value would break Radix. */}
                              {typeof field.value === "string" &&
                                field.value &&
                                !(emissionRegions.data?.regions ?? []).some(
                                  (r) => r.code === field.value,
                                ) && (
                                  <SelectItem value={field.value}>{field.value}</SelectItem>
                                )}
                              {(emissionRegions.data?.regions ?? []).map((r) => (
                                <SelectItem key={r.code} value={r.code}>
                                  {r.code} · {r.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormItem>
                      )}
                    />
                  )}
                </div>
              </div>

              {/* Tenure — create-only (managed on Overview afterwards) */}
              {!isEdit && !isSection && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {t("sections.tenure")}
                    </h3>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">{t("tenure.include")}</span>
                      <Switch
                        checked={includeTenure}
                        onCheckedChange={(checked) => {
                          setIncludeTenure(checked);
                          form.setValue(
                            "tenure",
                            checked
                              ? {
                                  tenure_type: "long_term_lease",
                                  valid_from: format(new Date(), "yyyy-MM-dd"),
                                }
                              : null,
                          );
                        }}
                      />
                    </div>
                  </div>
                  {includeTenure && (
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                      <FormField
                        control={form.control}
                        name="tenure.tenure_type"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <div className="flex items-center gap-2">
                              <FormLabel>{t("tenure.type")}</FormLabel>
                              {fieldState.error && (
                                <span className="text-xs text-destructive">{fieldState.error.message}</span>
                              )}
                            </div>
                            <Select value={field.value} onValueChange={field.onChange}>
                              <FormControl>
                                <SelectTrigger className={errorClass(!!fieldState.error)}>
                                  <SelectValue />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {TENURE_TYPES.map((tt) => (
                                  <SelectItem key={tt} value={tt}>
                                    {t(`tenureType.${tt}`)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="tenure.valid_from"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <div className="flex items-center gap-2">
                              <FormLabel>{t("tenure.validFrom")}</FormLabel>
                              {fieldState.error && (
                                <span className="text-xs text-destructive">{fieldState.error.message}</span>
                              )}
                            </div>
                            <FormControl>
                              <DatePicker
                                value={field.value ? parseLocalISODate(field.value) : undefined}
                                onChange={(d) => field.onChange(d ? format(d, "yyyy-MM-dd") : "")}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="tenure.valid_to"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <div className="flex items-center gap-2">
                              <FormLabel>{t("tenure.validTo")}</FormLabel>
                              {fieldState.error && (
                                <span className="text-xs text-destructive">{fieldState.error.message}</span>
                              )}
                            </div>
                            <FormControl>
                              <DatePicker
                                value={field.value ? parseLocalISODate(field.value) : undefined}
                                onChange={(d) => field.onChange(d ? format(d, "yyyy-MM-dd") : null)}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="tenure.cost"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <div className="flex items-center gap-2">
                              <FormLabel>{t("tenure.cost")}</FormLabel>
                              {fieldState.error && (
                                <span className="text-xs text-destructive">{fieldState.error.message}</span>
                              )}
                            </div>
                            <FormControl>
                              <NumberInput
                                className={errorClass(!!fieldState.error)}
                                value={field.value ?? ""}
                                onChange={(v) => field.onChange(v === "" ? null : Number(v))}
                                min={0}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="tenure.cost_period"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <div className="flex items-center gap-2">
                              <FormLabel>{t("tenure.costPeriod")}</FormLabel>
                              {fieldState.error && (
                                <span className="text-xs text-destructive">{fieldState.error.message}</span>
                              )}
                            </div>
                            <Select
                              value={field.value ?? undefined}
                              onValueChange={field.onChange}
                            >
                              <FormControl>
                                <SelectTrigger className={errorClass(!!fieldState.error)}>
                                  <SelectValue placeholder={t("tenure.costPeriodPlaceholder")} />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="monthly">{t("costPeriod.monthly")}</SelectItem>
                                <SelectItem value="quarterly">{t("costPeriod.quarterly")}</SelectItem>
                                <SelectItem value="annually">{t("costPeriod.annually")}</SelectItem>
                              </SelectContent>
                            </Select>
                          </FormItem>
                        )}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Notes */}
              <FormField
                control={form.control}
                name="notes"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel>{t("fields.notes")}</FormLabel>
                      {fieldState.error && (
                        <span className="text-xs text-destructive">{fieldState.error.message}</span>
                      )}
                    </div>
                    <FormControl>
                      <Textarea
                        value={field.value ?? ""}
                        onChange={(e) => field.onChange(e.target.value || null)}
                        placeholder={t("fields.notesPlaceholder")}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              {isEdit && (
                <>
                  {/* Profile facts (year built, occupancy %) are
                      effective-dated details, edited on the Facilities tab. */}
                  <p className="text-xs text-muted-foreground">
                    {t("edit.profileFactsMoved")}
                  </p>
                  <FormField
                    control={form.control}
                    name="is_active"
                    render={({ field }) => (
                      <FormItem className="flex items-center justify-between rounded-lg border p-3">
                        <FormLabel>{t("fields.isActive")}</FormLabel>
                        <FormControl>
                          <Switch
                            checked={field.value ?? true}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </>
              )}
            </CardContent>
          </Card>

          {/* Danger zone — edit mode only (shared ui primitive). */}
          {isEdit && place && (
            <DangerZone
              title={t("dangerZone.title")}
              actionTitle={t("dangerZone.deleteTitle")}
              description={t("dangerZone.deleteDescription")}
              buttonLabel={t("dangerZone.deleteButton")}
              onAction={() => setDeleteOpen(true)}
            />
          )}
        </div>
      </Form>

      {isEdit && place && deleteOpen && (
        <DeletePlaceDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          place={place}
          onDeleted={() => {
            setDeleteOpen(false);
            onOpenChange(false);
            onDeleted?.();
          }}
        />
      )}

      {mapPickerOpen && (
        <PlaceMapPickerDialog
          open={mapPickerOpen}
          onOpenChange={setMapPickerOpen}
          initialLocation={mapInitialLocation}
          initialCenter={mapInitialCenter}
          // Nothing is saved here — the pick is held until Submit.
          onConfirm={handleAddressSelect}
        />
      )}
    </FullscreenFormDialog>
  );
}
